import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CornerUpLeft,
  LayersSubtract,
  Cross,
  Checkbox,
  ArrowBackUp,
} from '@saleshandy/icons';
import { RootState } from '../../../../../../../../store/root-reducer';
import { Action } from '../../../../../../../../shared/design-system/components/organisms/table/types';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import { OnProspectSequenceActions } from '../../../../../../enums/prospect';
import AddToSequenceModal from '../../../modals/add-to-sequence-modal';
import { Images } from '../../../../../../../../shared/app-constants';
import { getIsRequestPending } from '../../../../../../../../shared/utils/get-request-status';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import store from '../../../../../../../../store/store';
import { mapSequenceStatusToContactFilter } from '../../../../../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/helpers/step-filter-mapper';
import { ResourceIdentifiers } from '../../../../../../../../shared/utils/access-control/enums/resource-identifiers';
import hasResource from '../../../../../../../../shared/utils/access-control/has-resource';
import planPermissionError from '../../../../../../../../shared/utils/errors/plan-permission-error';
import { isFreePlan } from '../../../../../../../../shared/utils/plans';
import AddProspectSequence from './helper/add-prospect-sequence';
import ProspectRemoveFromSequenceAlertModal from './helper/prospect-remove-from-sequence-alert';
import ProspectSequenceListTableSkeleton from './prospect-sequence-list-table-skeleton';
import NoProspectDetailResultScreen from '../../../no-prospect-detail-result-screen/no-prospect-detail-result-screen';
import ProspectSequenceList from './helper/prospect-seqeunce-list';
import { ADD_SEQUENCE_ERROR_MAP } from '../../../../utils/helper';
import { setLoadedSkeletonForCurrrentProspect } from '../../../../../../prospect-slice';

type IProps = {
  selectedProspectSequences: any[];
  isSingleProspectSubscribed: boolean;
  sendSequenceMarkAsRepliedRequest: (stepId: number) => void;
  sendRemoveFromSequenceRequest: (sequenceId: number) => void;
  sendAddToStepRequest: (sequenceId, stepId) => void;
  sendGetSingleProspectSequencesDetailsRequest: () => void;
  sendSequenceMarkAsFinishedRequest: (stepId: number) => void;
  getSingleProspectSequencesDetailsStatus: RequestStatus;
  allOutcomes: any;
  getSequenceListRequest?: () => void;
  getStepListRequest?: ({ sequenceId }) => void;
  addToStepRequestStatus?: RequestStatus;
  addToStepRequestSuccessfulCount?: number;
  addToStepRequestFailedCount?: number;
  addToStepRequestMessage?: string;
  initialIsAddSequenceExpanded?: boolean;
  onAddSequenceExpanded?: () => void;
  onActivityChange?: (value: boolean) => void;
  prospectActivityCount?: {
    notes: string | number;
    tasks: string | number;
    emails: string | number;
    sequences: string | number;
  };
  editProspectAcitivityCount?: (payload: any) => void;
};

const ProspectSequenceListTable: React.FC<IProps> = ({
  selectedProspectSequences,
  sendSequenceMarkAsRepliedRequest,
  sendAddToStepRequest,
  sendRemoveFromSequenceRequest,
  sendGetSingleProspectSequencesDetailsRequest,
  isSingleProspectSubscribed,
  sendSequenceMarkAsFinishedRequest,
  getSingleProspectSequencesDetailsStatus,
  allOutcomes,
  getSequenceListRequest,
  getStepListRequest,
  addToStepRequestStatus,
  addToStepRequestSuccessfulCount,
  addToStepRequestFailedCount,
  addToStepRequestMessage,
  initialIsAddSequenceExpanded,
  onActivityChange,
  prospectActivityCount,
  editProspectAcitivityCount,
}) => {
  // const state = store.getState();
  // const outcomeChangeRequestStatus =
  //   state.sequence.onOutcomeChangeRequest.status;

  const dispatch = useDispatch();

  // Get sequence action statuses from Redux
  const outcomeChangeRequestStatus = useSelector(
    (state: RootState) => state.sequence.onOutcomeChangeRequest.status,
  );
  const singleProspectSequencesMarkAsRepliedRequestStatus = useSelector(
    (state: RootState) =>
      state.prospect.singleProspectSequencesMarkAsRepliedRequest?.status,
  );
  const singleProspectSequencesMarkAsFinishedRequestStatus = useSelector(
    (state: RootState) =>
      state.prospect.singleProspectSequencesMarkAsFinishedRequest?.status,
  );
  const singleProspectRemoveFromSequencesRequestStatus = useSelector(
    (state: RootState) =>
      state.prospect.singleProspectRemoveFromSequencesRequest?.status,
  );
  const addToStepRequestResultReason = useSelector(
    (state: RootState) =>
      state.prospect?.addToStepRequestResultCount?.resultData?.failedReport,
  );
  const loadedSkeletonForCurrrentProspect = useSelector(
    (state: RootState) => state.prospect.loadedSkeletonForCurrrentProspect,
  );

  const [showAddToStepModal, setShowAddToStepModal] = useState<boolean>(false);
  const [sequence, setSequence] = useState<Record<string, number | string>>(
    null,
  );
  const [
    showProspectRemoveFromSequenceModal,
    setShowProspectRemoveFromSequenceModal,
  ] = useState<boolean>(false);
  const [rowToRemove, setRowToRemove] = useState(null);
  const [moveToAnotherStep, setMoveToAnotherStep] = useState(null);
  const [isAddSequenceExpanded, setIsAddSequenceExpanded] = useState(
    initialIsAddSequenceExpanded,
  );
  const [selectedOutcome, setSelectedOutcome] = useState(null);

  const addSequenceErrorMessage = (reason?: string) => {
    if (!reason) return 'Prospect already exists in the selected step';

    return (
      ADD_SEQUENCE_ERROR_MAP[reason] ||
      'Prospect already exists in the selected step'
    );
  };

  //* useEffect
  useEffect(() => {
    sendGetSingleProspectSequencesDetailsRequest();
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: outcomeChangeRequestStatus,
      onSuccess: () => {
        sendGetSingleProspectSequencesDetailsRequest();
      },
    });
  }, [outcomeChangeRequestStatus]);

  // Track mark as replied action
  useEffect(() => {
    if (
      singleProspectSequencesMarkAsRepliedRequestStatus ===
      RequestStatus.Succeeded
    ) {
      onActivityChange?.(true);
    }
  }, [singleProspectSequencesMarkAsRepliedRequestStatus, onActivityChange]);

  // Track mark as finished action
  useEffect(() => {
    if (
      singleProspectSequencesMarkAsFinishedRequestStatus ===
      RequestStatus.Succeeded
    ) {
      onActivityChange?.(true);
    }
  }, [singleProspectSequencesMarkAsFinishedRequestStatus, onActivityChange]);

  // Track remove from sequence action
  useEffect(() => {
    if (
      singleProspectRemoveFromSequencesRequestStatus === RequestStatus.Succeeded
    ) {
      onActivityChange?.(true);
    }
  }, [singleProspectRemoveFromSequencesRequestStatus, onActivityChange]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addToStepRequestStatus,
      onSuccess: () => {
        editProspectAcitivityCount({
          ...prospectActivityCount,
          sequences:
            Number(prospectActivityCount?.sequences) >= 0
              ? (Number(prospectActivityCount?.sequences) || 0) + 1
              : 1,
        });
        if (addToStepRequestSuccessfulCount > 0) {
          toaster.success(
            addToStepRequestMessage?.replace(/contact/gi, 'Prospect'),
          );
          onActivityChange?.(true);
        }
        if (addToStepRequestFailedCount > 0) {
          toaster.error(
            addSequenceErrorMessage(addToStepRequestResultReason?.[0]?.reason),
            {
              theme: Theme.New,
            },
          );
        }
      },
    });
  }, [addToStepRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getSingleProspectSequencesDetailsStatus,
      onSuccess: () => {
        dispatch(
          setLoadedSkeletonForCurrrentProspect({
            sequences: true,
          }),
        );
      },
    });
  }, [getSingleProspectSequencesDetailsStatus]);

  const hideAddToStepModal = () => {
    setShowAddToStepModal(false);
  };

  const addToSequenceStepRequest = (sequenceId, stepId) => {
    if (
      isFreePlan() &&
      !hasResource(ResourceIdentifiers.SEQUENCES_CONTACTS_IMPORT)
    ) {
      const title = 'Please upgrade your plan to start adding prospects';
      const content = [
        'Your account is downgraded to a free plan. Upgrade to paid plan to add prospects and resume email sending.',
      ];

      planPermissionError.showModal(title, content, true);
      return;
    }
    sendAddToStepRequest(sequenceId, stepId);
  };

  const data = selectedProspectSequences.map(({ step, sequenceProspect }) => {
    const sequences = {
      value: step.sequence.title,
      id: sequenceProspect.sequenceId,
      stepId: step.id,
      stepNumber: `Step ${step.number}`,
      type: step.sequence?.type,
      parentSequence: {
        ...(step.sequence?.parentSequence ?? {}),
        id: sequenceProspect?.parentSequenceId,
      },
      hideStepNumber:
        sequenceProspect.sequenceId !== sequenceProspect?.parentSequenceId &&
        step.sequence?.type === 'sequence',
      prospectId: sequenceProspect?.prospectId,
      sequenceProspectDealValue: sequenceProspect?.dealValue,
      outcomeId: sequenceProspect?.categoryId,
      userFirstName:
        step.sequence.user && step.sequence.user.firstName
          ? step.sequence.user.firstName
          : '',
      userLastName:
        step.sequence.user && step.sequence.user.firstName
          ? step.sequence.user.lastName
          : '',
      allOutcomes: allOutcomes || [],
      step,
    };
    return {
      id: step.id,
      sequences,
      status: {
        value: mapSequenceStatusToContactFilter(sequenceProspect?.finalStatus),
        updatedAt: sequenceProspect?.updatedAt,
      },
    };
  });

  data.reverse();

  const onConfirmRemoveFromSequence = () => {
    if (!rowToRemove) return;

    sendRemoveFromSequenceRequest(rowToRemove?.sequences?.parentSequence?.id);
    setShowProspectRemoveFromSequenceModal(false);
    setRowToRemove(null);
  };

  const onAction = (action: string, row: any) => {
    switch (action) {
      case OnProspectSequenceActions.MarkAsReplied:
        sendSequenceMarkAsRepliedRequest(row.sequences.stepId);
        break;

      case OnProspectSequenceActions.MoveToStep: {
        const seq =
          row?.sequences?.type === 'subsequence'
            ? {
                id: row.sequences?.parentSequence.id,
                title: row.sequences?.parentSequence.title,
              }
            : {
                id: row.sequences.id,
                title: row.sequences.value,
              };

        setMoveToAnotherStep(seq);
        break;
      }

      case OnProspectSequenceActions.RemoveFromSequence:
        setRowToRemove(row);
        setShowProspectRemoveFromSequenceModal(true);
        editProspectAcitivityCount({
          ...prospectActivityCount,
          sequences:
            Number(prospectActivityCount?.sequences) >= 0
              ? (Number(prospectActivityCount?.sequences) || 0) - 1
              : 1,
        });
        break;

      case OnProspectSequenceActions.MarkAsFinished:
        sendSequenceMarkAsFinishedRequest(row.sequences.stepId);
        break;
      default:
        break;
    }
  };

  const onClearSelection = () => {
    setMoveToAnotherStep(null);
  };

  const onCloseProspectRemoveFromSequenceModal = () => {
    setShowProspectRemoveFromSequenceModal(false);
  };

  const generateActionsColumn = (row): Action[] =>
    [
      {
        key: OnProspectSequenceActions.MarkAsReplied,
        displayName: 'Mark as Replied',
        isSVGIcon: true,
        SVGIcon: <ArrowBackUp width={16} height={16} className="gray-txt-15" />,
      },
      {
        key: OnProspectSequenceActions.MoveToStep,
        displayName: 'Move to Step',
        isSVGIcon: true,
        SVGIcon: <LayersSubtract className="gray-txt-15" />,
      },
      row?.sequences?.type !== 'subsequence' && {
        key: OnProspectSequenceActions.RemoveFromSequence,
        displayName: 'Remove from Sequence',
        isSVGIcon: true,
        SVGIcon: <Cross className="gray-txt-15" />,
      },
      {
        key: OnProspectSequenceActions.MarkAsFinished,
        displayName: 'Mark as Finished',
        isSVGIcon: true,
        SVGIcon: <Checkbox className="gray-txt-15" />,
      },
    ].filter(Boolean);

  if (
    !loadedSkeletonForCurrrentProspect?.sequences &&
    getIsRequestPending(getSingleProspectSequencesDetailsStatus)
  ) {
    return <ProspectSequenceListTableSkeleton />;
  }
  return (
    <div
      className={`${
        selectedProspectSequences.length === 0 && !isAddSequenceExpanded
          ? 'prospect-sequence-no-result-container'
          : 'h-100'
      }`}
    >
      <AddProspectSequence
        selectedProspectSequences={selectedProspectSequences}
        allOutcomes={allOutcomes}
        isSingleProspectSubscribed={isSingleProspectSubscribed}
        sendAddToStepRequest={addToSequenceStepRequest}
        sendGetSingleProspectSequencesDetailsRequest={
          sendGetSingleProspectSequencesDetailsRequest
        }
        selectedSequenceDefault={moveToAnotherStep}
        lockSelectedSequence={!!moveToAnotherStep}
        onClearSelection={onClearSelection}
        onExpandedChange={setIsAddSequenceExpanded}
        getSequenceListRequest={getSequenceListRequest}
        getStepListRequest={getStepListRequest}
        initialIsExpanded={initialIsAddSequenceExpanded}
      />
      {selectedProspectSequences.length === 0 && !isAddSequenceExpanded ? (
        <NoProspectDetailResultScreen
          title="Not added to any sequence"
          description="Add this prospect to a sequence to start your outreach journey."
          imageSrc={Images.NoProspectSequence}
        />
      ) : (
        <React.Fragment key={`prospect-sequence-list-table-${data?.length}`}>
          {data?.map((row) => (
            <ProspectSequenceList
              row={row}
              onAction={onAction}
              selectedOutcome={selectedOutcome}
              allOutcomes={allOutcomes}
              setSelectedOutcome={setSelectedOutcome}
              generateActionsColumn={generateActionsColumn}
            />
          ))}
        </React.Fragment>
      )}

      <ProspectRemoveFromSequenceAlertModal
        show={showProspectRemoveFromSequenceModal}
        onCancel={onCloseProspectRemoveFromSequenceModal}
        onHide={onConfirmRemoveFromSequence}
      />

      {showAddToStepModal && (
        <AddToSequenceModal
          show={showAddToStepModal}
          onHide={hideAddToStepModal}
          addToSequence={addToSequenceStepRequest}
          sequence={sequence}
          isSubscribed={isSingleProspectSubscribed}
        />
      )}
    </div>
  );
};

export default ProspectSequenceListTable;
